/**
 * SSOComplete — receives the Beacon access token from the URL fragment
 * after the IdP callback redirects the browser here:
 *   /sso/complete#token=<jwt>
 *
 * Reads the fragment, stores the token, then navigates to Beacon home.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { BeaconMark, BeaconWordmark } from '@/components/BeaconBrand'
import { setAuthSession } from '@/utils/auth'

export default function SSOComplete() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fragment = window.location.hash.slice(1)  // strip leading #
    const params = new URLSearchParams(fragment)
    const token = params.get('token')

    if (!token) {
      setError('No access token found in the redirect URL. Please try logging in again.')
      setStatus('error')
      return
    }

    // Store the token — same key the rest of the app uses
    setAuthSession({ accessToken: token, idToken: null, refreshToken: null })
    setStatus('success')

    // Clear the fragment from the URL to avoid token leaking in browser history
    window.history.replaceState(null, '', window.location.pathname)

    const timer = setTimeout(() => navigate('/'), 1500)
    return () => clearTimeout(timer)
  }, [navigate])

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <BeaconMark size={40} />
          <BeaconWordmark className="text-2xl font-bold" textClassName="text-white" />
        </div>

        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl">
          {status === 'loading' && (
            <>
              <Loader2 size={40} className="animate-spin text-beacon-orange mx-auto mb-4" />
              <p className="text-gray-300 font-medium">Completing sign-in…</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
              <p className="text-white font-semibold text-lg mb-1">Signed in successfully</p>
              <p className="text-gray-400 text-sm">Redirecting you to Beacon…</p>
            </>
          )}
          {status === 'error' && (
            <>
              <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
              <p className="text-white font-semibold text-lg mb-2">Sign-in failed</p>
              <p className="text-gray-400 text-sm mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
